import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Copy, ExternalLink, Inbox, Mail, Pencil, Plus, RefreshCw, Send, ShieldCheck, Trash2, XCircle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Tabs } from '@/components/tickets';
import { Button } from '@/components/ui/button';
import { useConfirm } from '@/components/ui/confirm';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Field, PageHeader } from '@/components/ui/form-field';
import { Alert, Badge, Card, CardContent, CardDescription, CardHeader, CardTitle, Checkbox, Input, Select, Skeleton, Switch } from '@/components/ui/primitives';
import { EmptyRow, Pagination, Table, TBody, TD, TH, THead, TR } from '@/components/ui/table';
import { api, errorMessage } from '@/lib/api';
import type { AreaConfig, ConnectionStatus, EmailItem, EmailStatus, M365Overview, MailboxItem, MailboxRule, Paginated, TestResult } from '@/lib/types';
import { formatDate, formatDateTime } from '@/lib/utils';
import { M365Wizard } from './m365-wizard';

const KEY = ['integrations', 'm365'];
const onError = (e: unknown) => toast.error(errorMessage(e));

function CopyValue({ value, label }: { value: string; label: string }) {
  return (
    <div className="flex items-center gap-2">
      <code className="min-w-0 flex-1 truncate rounded-md border bg-muted/50 px-2 py-1.5 text-xs" title={value}>
        {value}
      </code>
      <Button
        type="button"
        variant="outline"
        size="icon"
        className="size-8 shrink-0"
        aria-label={`${label} kopieren`}
        onClick={() => navigator.clipboard.writeText(value).then(() => toast.success(`${label} kopiert`), () => toast.error('Kopieren nicht möglich'))}
      >
        <Copy />
      </Button>
    </div>
  );
}

function StatusBadge({ status }: { status: ConnectionStatus }) {
  if (status === 'OK') return <Badge variant="success">Verbunden</Badge>;
  if (status === 'ERROR') return <Badge variant="destructive">Fehler</Badge>;
  return <Badge variant="outline">Nicht geprüft</Badge>;
}

// ---------------------------------------------------------------------------
// Schritt 1: Anleitung
// ---------------------------------------------------------------------------

function SetupGuide({ data }: { data: M365Overview }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>App in Microsoft Entra ID registrieren</CardTitle>
        <CardDescription>Nur nötig, wenn der Assistent nicht verwendet werden kann (z. B. weil Richtlinien die Gerätecode-Anmeldung sperren). Danach die Werte oben unter „Verbindung“ eintragen.</CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4 text-sm">
        <ol className="grid min-w-0 list-decimal gap-2 break-words pl-5 [overflow-wrap:anywhere]">
          <li>
            Im{' '}
            <a className="text-primary underline-offset-2 hover:underline" href="https://entra.microsoft.com/#view/Microsoft_AAD_RegisteredApps/ApplicationsListBlade" target="_blank" rel="noreferrer">
              Microsoft Entra Admin Center <ExternalLink className="inline size-3" />
            </a>{' '}
            unter <em>App-Registrierungen → Neue Registrierung</em> eine App anlegen (z. B. „Serviceportal“, nur Konten in diesem Organisationsverzeichnis).
          </li>
          <li>
            Als Umleitungs-URI vom Typ <strong>Web</strong> beide Adressen eintragen:
            <div className="mt-2 grid gap-2">
              <CopyValue value={data.setup.consentRedirectUri} label="Umleitungs-URI (Zustimmung)" />
              <CopyValue value={data.setup.loginRedirectUri} label="Umleitungs-URI (Anmeldung)" />
            </div>
          </li>
          <li>
            Unter <em>API-Berechtigungen → Microsoft Graph → Anwendungsberechtigungen</em> hinzufügen:
            <ul className="mt-2 grid gap-1">
              {data.setup.requiredPermissions.map((p) => (
                <li key={p.key} className="flex flex-wrap gap-x-2">
                  <code className="font-semibold">{p.key}</code>
                  <span className="text-muted-foreground">– {p.purpose}</span>
                </li>
              ))}
            </ul>
            <p className="mt-1 text-muted-foreground">
              Für „Mit Microsoft anmelden“ zusätzlich die delegierten Berechtigungen {data.setup.loginPermissions.map((p) => <code key={p} className="mx-0.5">{p}</code>)}.
            </p>
          </li>
          <li>
            Unter <em>Zertifikate &amp; Geheimnisse</em> einen geheimen Clientschlüssel erstellen und den <strong>Wert</strong> unten eintragen (er wird verschlüsselt gespeichert und nicht wieder angezeigt).
          </li>
          <li>Verzeichnis-ID (Mandanten-ID) und Anwendungs-ID (Client-ID) von der Übersichtsseite der App übernehmen.</li>
        </ol>
        <Alert>
          <strong>Empfehlung:</strong> Den Postfachzugriff der App per Exchange-Online-Zugriffsrichtlinie (RBAC für Anwendungen) auf die Service-Postfächer und die Kalender
          der Mitarbeitenden beschränken. Ohne Einschränkung gelten Anwendungsberechtigungen für alle Postfächer der Organisation.
        </Alert>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Schritt 2: Verbindung
// ---------------------------------------------------------------------------

interface ConnectionForm {
  directoryId: string;
  clientId: string;
  clientSecret: string;
  clientSecretExpiresAt: string;
  calendarEnabled: boolean;
  loginEnabled: boolean;
}

function ConnectionCard({ data }: { data: M365Overview }) {
  const qc = useQueryClient();
  const c = data.connection;
  const [form, setForm] = useState<ConnectionForm>({ directoryId: '', clientId: '', clientSecret: '', clientSecretExpiresAt: '', calendarEnabled: true, loginEnabled: false });
  const [test, setTest] = useState<TestResult | null>(null);
  const [confirmDialog, confirm] = useConfirm();

  useEffect(() => {
    setForm({
      directoryId: c?.directoryId ?? '',
      clientId: c?.clientId ?? '',
      clientSecret: '',
      clientSecretExpiresAt: c?.clientSecretExpiresAt?.slice(0, 10) ?? '',
      calendarEnabled: c?.calendarEnabled ?? true,
      loginEnabled: c?.loginEnabled ?? false,
    });
  }, [c]);

  const set = <K extends keyof ConnectionForm>(k: K, v: ConnectionForm[K]) => setForm((f) => ({ ...f, [k]: v }));
  const refresh = () => qc.invalidateQueries({ queryKey: KEY });

  const save = useMutation({
    mutationFn: () =>
      api('/integrations/m365/connection', {
        method: 'PUT',
        body: {
          directoryId: form.directoryId.trim(),
          clientId: form.clientId.trim(),
          clientSecret: form.clientSecret.trim() || undefined,
          clientSecretExpiresAt: form.clientSecretExpiresAt || null,
          calendarEnabled: form.calendarEnabled,
          loginEnabled: form.loginEnabled,
        },
      }),
    onSuccess: () => {
      toast.success('Verbindung gespeichert');
      setTest(null);
      refresh();
    },
    onError,
  });
  const runTest = useMutation({
    mutationFn: () => api<TestResult>('/integrations/m365/connection/test', { method: 'POST' }),
    onSuccess: (r) => {
      setTest(r);
      refresh();
    },
    onError,
  });
  const consent = useMutation({
    mutationFn: () => api<{ url: string }>('/integrations/m365/connection/consent', { method: 'POST' }),
    onSuccess: (r) => window.location.assign(r.url),
    onError,
  });
  const remove = useMutation({
    mutationFn: () => api('/integrations/m365/connection', { method: 'DELETE' }),
    onSuccess: () => {
      toast.success('Verbindung entfernt');
      setTest(null);
      refresh();
    },
    onError,
  });

  const expiresInDays = c?.clientSecretExpiresAt ? Math.ceil((new Date(c.clientSecretExpiresAt).getTime() - Date.now()) / 86_400_000) : null;
  const dirty =
    !c ||
    form.clientSecret.trim() !== '' ||
    form.directoryId !== c.directoryId ||
    form.clientId !== c.clientId ||
    form.calendarEnabled !== c.calendarEnabled ||
    form.loginEnabled !== c.loginEnabled ||
    form.clientSecretExpiresAt !== (c.clientSecretExpiresAt?.slice(0, 10) ?? '');

  return (
    <Card>
      {confirmDialog}
      <CardHeader className="flex-row items-start justify-between gap-3 space-y-0">
        <div>
          <CardTitle>Verbindung</CardTitle>
          <CardDescription>Zugangsdaten der App-Registrierung (vom Assistenten automatisch ausgefüllt)</CardDescription>
        </div>
        {c && <StatusBadge status={c.status} />}
      </CardHeader>
      <CardContent className="grid gap-5">
        <form
          className="grid gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
        >
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Verzeichnis-ID (Mandanten-ID)" htmlFor="m-dir">
              <Input id="m-dir" className="font-mono" placeholder="00000000-0000-0000-0000-000000000000" value={form.directoryId} onChange={(e) => set('directoryId', e.target.value)} autoComplete="off" />
            </Field>
            <Field label="Anwendungs-ID (Client-ID)" htmlFor="m-client">
              <Input id="m-client" className="font-mono" placeholder="00000000-0000-0000-0000-000000000000" value={form.clientId} onChange={(e) => set('clientId', e.target.value)} autoComplete="off" />
            </Field>
            <Field label="Geheimer Clientschlüssel (Wert)" htmlFor="m-secret" hint={c ? 'Gespeichert. Nur zum Erneuern ausfüllen.' : undefined}>
              <Input
                id="m-secret"
                type="password"
                placeholder={c ? '•••••••••••• (unverändert)' : ''}
                value={form.clientSecret}
                onChange={(e) => set('clientSecret', e.target.value)}
                autoComplete="new-password"
              />
            </Field>
            <Field label="Schlüssel gültig bis (optional)" htmlFor="m-exp" hint="Wird 30 Tage vor Ablauf auf dieser Seite hervorgehoben.">
              <Input id="m-exp" type="date" value={form.clientSecretExpiresAt} onChange={(e) => set('clientSecretExpiresAt', e.target.value)} />
            </Field>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="flex items-start gap-3 rounded-md border p-3 text-sm">
              <Switch checked={form.calendarEnabled} onCheckedChange={(v) => set('calendarEnabled', v)} className="mt-0.5" />
              <span>
                <span className="font-medium">Outlook-Kalender</span>
                <span className="block text-muted-foreground">Termine in die Kalender der Mitarbeitenden eintragen, Verfügbarkeit prüfen, Teams-Besprechungen.</span>
              </span>
            </label>
            <label className="flex items-start gap-3 rounded-md border p-3 text-sm">
              <Switch checked={form.loginEnabled} onCheckedChange={(v) => set('loginEnabled', v)} className="mt-0.5" />
              <span>
                <span className="font-medium">Mit Microsoft anmelden</span>
                <span className="block text-muted-foreground">Mitarbeitende melden sich mit ihrem Microsoft-Konto an (nur bestehende Benutzer mit gleicher E-Mail-Adresse).</span>
              </span>
            </label>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button type="submit" loading={save.isPending} disabled={!dirty || !form.directoryId || !form.clientId || (!c && !form.clientSecret)}>
              Speichern
            </Button>
            {c && (
              <>
                <Button type="button" variant="outline" onClick={() => runTest.mutate()} loading={runTest.isPending} disabled={dirty}>
                  <CheckCircle2 /> Verbindung testen
                </Button>
                <Button type="button" variant="outline" onClick={() => consent.mutate()} loading={consent.isPending} disabled={dirty}>
                  <ShieldCheck /> Administratorzustimmung erteilen
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  className="text-destructive hover:text-destructive sm:ml-auto"
                  onClick={async () =>
                    (await confirm({
                      title: 'Verbindung entfernen?',
                      description: 'Alle Postfächer werden entfernt; E-Mail-Abruf, Versand über Microsoft 365, Outlook-Abgleich und Microsoft-Anmeldung enden. Der E-Mail-Verlauf bleibt erhalten.',
                      confirmLabel: 'Entfernen',
                      destructive: true,
                    })) && remove.mutate()
                  }
                >
                  <Trash2 /> Entfernen
                </Button>
              </>
            )}
          </div>
        </form>

        {c && (
          <dl className="grid gap-x-6 gap-y-1 text-sm sm:grid-cols-[auto_1fr]">
            <dt className="text-muted-foreground">Administratorzustimmung</dt>
            <dd>{c.consentGrantedAt ? `erteilt am ${formatDateTime(c.consentGrantedAt)}` : 'noch nicht über das Portal erteilt (kann auch direkt in Entra ID erfolgen)'}</dd>
            <dt className="text-muted-foreground">Zuletzt geprüft</dt>
            <dd>{formatDateTime(c.lastCheckedAt)}</dd>
            {c.clientSecretExpiresAt && (
              <>
                <dt className="text-muted-foreground">Schlüssel gültig bis</dt>
                <dd className={expiresInDays !== null && expiresInDays <= 30 ? 'font-medium text-destructive' : undefined}>
                  {formatDate(c.clientSecretExpiresAt)}
                  {expiresInDays !== null && expiresInDays <= 30 && ` – ${expiresInDays <= 0 ? 'abgelaufen' : `läuft in ${expiresInDays} Tagen ab`}`}
                </dd>
              </>
            )}
          </dl>
        )}
        {test ? (
          <Alert variant={test.ok ? 'success' : 'destructive'}>
            <p className="font-medium">{test.message}</p>
            {test.grantedPermissions && (
              <ul className="mt-2 flex flex-wrap gap-1.5">
                {data.setup.requiredPermissions.map((p) => (
                  <li key={p.key}>
                    <Badge variant={test.missingPermissions?.includes(p.key) ? 'destructive' : 'success'}>
                      {test.missingPermissions?.includes(p.key) ? <XCircle className="size-3" /> : <CheckCircle2 className="size-3" />}
                      {p.key}
                    </Badge>
                  </li>
                ))}
              </ul>
            )}
          </Alert>
        ) : (
          c?.status === 'ERROR' && c.lastError && <Alert variant="destructive">{c.lastError}</Alert>
        )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Schritt 3: Postfächer
// ---------------------------------------------------------------------------

type MailboxForm = Omit<MailboxItem, 'id' | 'lastSyncAt' | 'lastError' | 'createdAt'>;
const emptyMailbox: MailboxForm = {
  address: '',
  displayName: '',
  areaId: null,
  rules: [],
  syncEnabled: true,
  isDefaultSender: false,
  senderAreaIds: [],
  senderForRepairs: false,
  acknowledge: true,
  markAsRead: true,
};

const RULE_LABELS: Record<MailboxRule['match'], string> = {
  senderDomain: 'Absender-Domain ist',
  senderAddress: 'Absender-Adresse ist',
  subjectContains: 'Betreff enthält',
};

function MailboxDialog({ edit, areas, onClose }: { edit: { id: string | null; form: MailboxForm } | null; areas: AreaConfig[]; onClose: () => void }) {
  const qc = useQueryClient();
  const [form, setForm] = useState<MailboxForm>(emptyMailbox);
  useEffect(() => {
    if (edit) setForm(edit.form);
  }, [edit]);
  const set = <K extends keyof MailboxForm>(k: K, v: MailboxForm[K]) => setForm((f) => ({ ...f, [k]: v }));
  const setRule = (i: number, patch: Partial<MailboxRule>) => set('rules', form.rules.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const save = useMutation({
    mutationFn: () => {
      const body = { ...form, displayName: form.displayName || null, rules: form.rules.filter((r) => r.value.trim() && r.areaId) };
      return edit?.id ? api(`/integrations/m365/mailboxes/${edit.id}`, { method: 'PUT', body }) : api('/integrations/m365/mailboxes', { method: 'POST', body });
    },
    onSuccess: () => {
      toast.success('Postfach gespeichert');
      qc.invalidateQueries({ queryKey: KEY });
      onClose();
    },
    onError,
  });
  const activeAreas = areas.filter((a) => a.active);

  return (
    <Dialog open={edit !== null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent wide>
        <form
          className="flex min-h-0 flex-col"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
        >
          <DialogHeader>
            <DialogTitle>{edit?.id ? 'Postfach bearbeiten' : 'Postfach hinzufügen'}</DialogTitle>
            <DialogDescription>Ein Exchange-Online-Postfach (z. B. ein freigegebenes Postfach „service@…“).</DialogDescription>
          </DialogHeader>
          <DialogBody className="grid gap-5">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="E-Mail-Adresse" htmlFor="mb-address">
                <Input id="mb-address" type="email" required value={form.address} onChange={(e) => set('address', e.target.value)} />
              </Field>
              <Field label="Anzeigename (optional)" htmlFor="mb-name">
                <Input id="mb-name" value={form.displayName ?? ''} onChange={(e) => set('displayName', e.target.value)} />
              </Field>
              <Field label="Neue Tickets im Bereich" htmlFor="mb-area" hint="Ohne Bereich werden neue Anfragen nur protokolliert, Antworten aber zugeordnet." className="sm:col-span-2">
                <Select id="mb-area" value={form.areaId ?? ''} onChange={(e) => set('areaId', e.target.value || null)}>
                  <option value="">– kein Bereich –</option>
                  {activeAreas.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>

            <fieldset className="grid gap-2">
              <legend className="mb-1 text-sm font-medium">Zuordnungsregeln (erste passende Regel gewinnt)</legend>
              {form.rules.length === 0 && <p className="text-sm text-muted-foreground">Keine Regeln – alle neuen Anfragen gehen in den oben gewählten Bereich.</p>}
              {form.rules.map((r, i) => (
                <div key={i} className="grid gap-2 rounded-md border p-2 sm:grid-cols-[12rem_1fr_12rem_auto]">
                  <Select aria-label="Bedingung" value={r.match} onChange={(e) => setRule(i, { match: e.target.value as MailboxRule['match'] })}>
                    {Object.entries(RULE_LABELS).map(([k, v]) => (
                      <option key={k} value={k}>
                        {v}
                      </option>
                    ))}
                  </Select>
                  <Input aria-label="Wert" placeholder={r.match === 'senderDomain' ? 'kunde.de' : r.match === 'senderAddress' ? 'it@kunde.de' : 'Reparatur'} value={r.value} onChange={(e) => setRule(i, { value: e.target.value })} />
                  <Select aria-label="Zielbereich" value={r.areaId} onChange={(e) => setRule(i, { areaId: e.target.value })}>
                    <option value="">Bereich wählen…</option>
                    {activeAreas.map((a) => (
                      <option key={a.id} value={a.id}>
                        → {a.name}
                      </option>
                    ))}
                  </Select>
                  <Button type="button" variant="ghost" size="icon" className="size-9" aria-label="Regel entfernen" onClick={() => set('rules', form.rules.filter((_, j) => j !== i))}>
                    <Trash2 />
                  </Button>
                </div>
              ))}
              <div>
                <Button type="button" size="sm" variant="outline" onClick={() => set('rules', [...form.rules, { match: 'senderDomain', value: '', areaId: '' }])}>
                  <Plus /> Regel
                </Button>
              </div>
            </fieldset>

            <div className="grid gap-3 sm:grid-cols-2">
              {(
                [
                  ['syncEnabled', 'Posteingang abrufen', 'Neue Nachrichten werden alle zwei Minuten abgerufen.'],
                  ['acknowledge', 'Eingangsbestätigung senden', 'Absender neuer Anfragen erhalten die Ticketnummer.'],
                  ['markAsRead', 'Als gelesen markieren', 'Verarbeitete Nachrichten im Postfach als gelesen kennzeichnen.'],
                  ['isDefaultSender', 'Standard-Absender', 'Alle Benachrichtigungen des Portals über dieses Postfach senden.'],
                ] as const
              ).map(([k, label, hint]) => (
                <label key={k} className="flex items-start gap-3 rounded-md border p-3 text-sm">
                  <Switch checked={form[k]} onCheckedChange={(v) => set(k, v)} className="mt-0.5" />
                  <span>
                    <span className="font-medium">{label}</span>
                    <span className="block text-muted-foreground">{hint}</span>
                  </span>
                </label>
              ))}
            </div>

            <fieldset className="grid gap-2 rounded-md border p-3 text-sm">
              <legend className="px-1 font-medium">Ausgangspostfach für</legend>
              <p className="text-muted-foreground">
                Mails zu Tickets dieser Bereiche bzw. zu Reparaturen gehen über dieses Postfach (statt über den Standard-Absender). Antworten auf Tickets, die per E-Mail eingegangen sind, gehen
                immer aus dem Postfach, über das die Anfrage kam. Je Bereich gibt es ein Ausgangspostfach – hier gewählte Bereiche werden bei anderen Postfächern entfernt.
              </p>
              <div className="flex flex-wrap gap-x-4 gap-y-2">
                {areas.map((a) => (
                  <label key={a.id} className="flex items-center gap-2">
                    <Checkbox
                      checked={form.senderAreaIds.includes(a.id)}
                      onCheckedChange={(v) => set('senderAreaIds', v ? [...form.senderAreaIds, a.id] : form.senderAreaIds.filter((x) => x !== a.id))}
                    />
                    Tickets „{a.name}“
                  </label>
                ))}
                <label className="flex items-center gap-2">
                  <Checkbox checked={form.senderForRepairs} onCheckedChange={(v) => set('senderForRepairs', v === true)} />
                  Reparaturen
                </label>
              </div>
            </fieldset>
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Abbrechen
            </Button>
            <Button type="submit" loading={save.isPending} disabled={!form.address}>
              Speichern
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function MailboxesCard({ data }: { data: M365Overview }) {
  const qc = useQueryClient();
  const { data: areas } = useQuery({ queryKey: ['ticket-config', 'areas'], queryFn: () => api<AreaConfig[]>('/ticket-config/areas') });
  const [edit, setEdit] = useState<{ id: string | null; form: MailboxForm } | null>(null);
  const [testMail, setTestMail] = useState<{ mailbox: MailboxItem; to: string } | null>(null);
  const [confirmDialog, confirm] = useConfirm();
  const refresh = () => qc.invalidateQueries({ queryKey: KEY });
  const show = (r: TestResult) => (r.ok ? toast.success(r.message) : toast.error(r.message, { duration: 10_000 }));

  const test = useMutation({ mutationFn: (id: string) => api<TestResult>(`/integrations/m365/mailboxes/${id}/test`, { method: 'POST' }), onSuccess: show, onError });
  const sync = useMutation({
    mutationFn: (id: string) => api<TestResult>(`/integrations/m365/mailboxes/${id}/sync`, { method: 'POST' }),
    onSuccess: (r) => {
      show(r);
      refresh();
      qc.invalidateQueries({ queryKey: ['integrations', 'm365', 'emails'] });
    },
    onError,
  });
  const sendTest = useMutation({
    mutationFn: () => api<TestResult>(`/integrations/m365/mailboxes/${testMail!.mailbox.id}/test-mail`, { method: 'POST', body: { to: testMail!.to } }),
    onSuccess: (r) => {
      show(r);
      if (r.ok) setTestMail(null);
    },
    onError,
  });
  const remove = useMutation({
    mutationFn: (id: string) => api(`/integrations/m365/mailboxes/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      toast.success('Postfach entfernt');
      refresh();
    },
    onError,
  });
  const areaName = (id: string | null) => areas?.find((a) => a.id === id)?.name;

  return (
    <Card>
      {confirmDialog}
      <CardHeader className="flex-row items-start justify-between gap-3 space-y-0">
        <div>
          <CardTitle>Postfächer</CardTitle>
          <CardDescription>E-Mails werden zu Tickets; Antworten mit Ticketnummer im Betreff landen im bestehenden Ticket.</CardDescription>
        </div>
        <Button size="sm" disabled={!data.connection} onClick={() => setEdit({ id: null, form: { ...emptyMailbox, isDefaultSender: data.mailboxes.length === 0 } })}>
          <Plus /> Postfach
        </Button>
      </CardHeader>
      <CardContent className="grid gap-3">
        {!data.connection && <p className="text-sm text-muted-foreground">Zuerst mit Microsoft 365 verbinden.</p>}
        {data.connection && data.mailboxes.length === 0 && <p className="text-sm text-muted-foreground">Noch kein Postfach eingerichtet.</p>}
        {data.mailboxes.map((m) => (
          <div key={m.id} className="rounded-lg border p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="flex flex-wrap items-center gap-2 font-medium">
                  <Inbox className="size-4 text-muted-foreground" />
                  {m.address}
                  {m.isDefaultSender && <Badge>Standard-Absender</Badge>}
                  {m.senderAreaIds.map((id) => (
                    <Badge key={id} variant="outline">
                      Absender {areaName(id) ?? 'Bereich'}
                    </Badge>
                  ))}
                  {m.senderForRepairs && <Badge variant="outline">Absender Reparaturen</Badge>}
                  {!m.syncEnabled && <Badge variant="outline">Abruf aus</Badge>}
                  {m.lastError ? <Badge variant="destructive">Fehler</Badge> : m.lastSyncAt ? <Badge variant="success">Abruf ok</Badge> : null}
                </p>
                <p className="mt-1 text-sm text-muted-foreground">
                  Bereich: {areaName(m.areaId) ?? 'keiner'}
                  {m.rules.length > 0 && ` · ${m.rules.length} Regel${m.rules.length === 1 ? '' : 'n'}`} · Zuletzt abgerufen: {formatDateTime(m.lastSyncAt)}
                </p>
                {m.lastError && <p className="mt-1 text-sm text-destructive">{m.lastError}</p>}
              </div>
              <div className="flex flex-wrap gap-1">
                <Button size="sm" variant="outline" onClick={() => sync.mutate(m.id)} loading={sync.isPending && sync.variables === m.id}>
                  <RefreshCw /> Jetzt abrufen
                </Button>
                <Button size="sm" variant="outline" onClick={() => test.mutate(m.id)} loading={test.isPending && test.variables === m.id}>
                  <CheckCircle2 /> Zugriff testen
                </Button>
                <Button size="sm" variant="outline" onClick={() => setTestMail({ mailbox: m, to: '' })}>
                  <Send /> Testnachricht
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  className="size-8"
                  aria-label={`${m.address} bearbeiten`}
                  onClick={() =>
                    setEdit({
                      id: m.id,
                      form: { address: m.address, displayName: m.displayName ?? '', areaId: m.areaId, rules: m.rules, syncEnabled: m.syncEnabled, isDefaultSender: m.isDefaultSender, senderAreaIds: m.senderAreaIds, senderForRepairs: m.senderForRepairs, acknowledge: m.acknowledge, markAsRead: m.markAsRead },
                    })
                  }
                >
                  <Pencil />
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  className="size-8 text-destructive hover:text-destructive"
                  aria-label={`${m.address} entfernen`}
                  onClick={async () => (await confirm({ title: `Postfach ${m.address} entfernen?`, description: 'Der bisherige E-Mail-Verlauf bleibt erhalten.', confirmLabel: 'Entfernen', destructive: true })) && remove.mutate(m.id)}
                >
                  <Trash2 />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </CardContent>

      <MailboxDialog edit={edit} areas={areas ?? []} onClose={() => setEdit(null)} />

      <Dialog open={testMail !== null} onOpenChange={(o) => !o && setTestMail(null)}>
        <DialogContent>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              sendTest.mutate();
            }}
          >
            <DialogHeader>
              <DialogTitle>Testnachricht senden</DialogTitle>
              <DialogDescription>Absender: {testMail?.mailbox.address}</DialogDescription>
            </DialogHeader>
            <DialogBody>
              <Field label="Empfänger" htmlFor="tm-to">
                <Input id="tm-to" type="email" required value={testMail?.to ?? ''} onChange={(e) => setTestMail((t) => t && { ...t, to: e.target.value })} />
              </Field>
            </DialogBody>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setTestMail(null)}>
                Abbrechen
              </Button>
              <Button type="submit" loading={sendTest.isPending} disabled={!testMail?.to}>
                <Mail /> Senden
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// E-Mail-Verlauf
// ---------------------------------------------------------------------------

const EMAIL_STATUS: Record<EmailStatus, { label: string; variant: 'success' | 'default' | 'outline' | 'destructive' | 'secondary' }> = {
  TICKET_CREATED: { label: 'Ticket erstellt', variant: 'success' },
  ADDED_TO_TICKET: { label: 'Zum Ticket', variant: 'default' },
  IGNORED: { label: 'Ignoriert', variant: 'outline' },
  SENT: { label: 'Gesendet', variant: 'secondary' },
  FAILED: { label: 'Fehlgeschlagen', variant: 'destructive' },
};

function EmailLog() {
  const [page, setPage] = useState(1);
  const [direction, setDirection] = useState('');
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const params = new URLSearchParams({ page: String(page), pageSize: '25', ...(direction && { direction }), ...(status && { status }), ...(q.trim() && { q: q.trim() }) });
  const { data } = useQuery({
    queryKey: ['integrations', 'm365', 'emails', params.toString()],
    queryFn: () => api<Paginated<EmailItem>>(`/integrations/m365/emails?${params}`),
  });
  const [detail, setDetail] = useState<string | null>(null);
  const { data: full } = useQuery({
    queryKey: ['integrations', 'm365', 'email', detail],
    queryFn: () => api<EmailItem & { bodyText: string }>(`/integrations/m365/emails/${detail}`),
    enabled: detail !== null,
  });

  return (
    <Card>
      <div className="flex flex-wrap gap-2 border-b p-3">
        <Input className="w-full sm:w-64" placeholder="Betreff oder Adresse" value={q} onChange={(e) => (setQ(e.target.value), setPage(1))} aria-label="Suche" />
        <Select className="w-auto" value={direction} onChange={(e) => (setDirection(e.target.value), setPage(1))} aria-label="Richtung">
          <option value="">Ein- und ausgehend</option>
          <option value="INBOUND">Eingehend</option>
          <option value="OUTBOUND">Ausgehend</option>
        </Select>
        <Select className="w-auto" value={status} onChange={(e) => (setStatus(e.target.value), setPage(1))} aria-label="Status">
          <option value="">Alle Status</option>
          {Object.entries(EMAIL_STATUS).map(([k, v]) => (
            <option key={k} value={k}>
              {v.label}
            </option>
          ))}
        </Select>
      </div>
      <Table>
        <THead>
          <TR className="hover:bg-transparent">
            <TH>Zeit</TH>
            <TH>Von / An</TH>
            <TH>Betreff</TH>
            <TH>Status</TH>
            <TH className="hidden md:table-cell">Ticket</TH>
          </TR>
        </THead>
        <TBody>
          {data?.items.length === 0 && <EmptyRow colSpan={5}>Noch keine E-Mails.</EmptyRow>}
          {data?.items.map((m) => (
            <TR key={m.id} className="cursor-pointer" onClick={() => setDetail(m.id)}>
              <TD className="whitespace-nowrap text-sm">{formatDateTime(m.receivedAt)}</TD>
              <TD className="max-w-[14rem] truncate text-sm">{m.direction === 'INBOUND' ? m.fromName ?? m.fromAddress : `→ ${m.toAddresses.join(', ')}`}</TD>
              <TD className="max-w-[22rem] truncate text-sm">{m.subject}</TD>
              <TD>
                <Badge variant={EMAIL_STATUS[m.status].variant}>{EMAIL_STATUS[m.status].label}</Badge>
              </TD>
              <TD className="hidden text-sm md:table-cell">
                {m.ticket ? (
                  <Link to={`/tickets/${m.ticket.id}`} className="text-primary hover:underline" onClick={(e) => e.stopPropagation()}>
                    {m.ticket.number}
                  </Link>
                ) : (
                  '–'
                )}
              </TD>
            </TR>
          ))}
        </TBody>
      </Table>
      {data && <Pagination page={page} pageSize={25} total={data.total} onPage={setPage} />}

      <Dialog open={detail !== null} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent wide>
          <DialogHeader>
            <DialogTitle className="pr-8">{full?.subject ?? 'E-Mail'}</DialogTitle>
            {full && (
              <DialogDescription>
                {full.direction === 'INBOUND' ? `Von ${full.fromName ? `${full.fromName} <${full.fromAddress}>` : full.fromAddress}` : `An ${full.toAddresses.join(', ')}`} ·{' '}
                {formatDateTime(full.receivedAt)} · {full.mailbox ?? 'Postfach entfernt'}
              </DialogDescription>
            )}
          </DialogHeader>
          <DialogBody className="grid gap-3">
            {!full ? (
              <Skeleton className="h-40" />
            ) : (
              <>
                <div className="flex flex-wrap gap-2">
                  <Badge variant={EMAIL_STATUS[full.status].variant}>{EMAIL_STATUS[full.status].label}</Badge>
                  {full.ticket && (
                    <Link to={`/tickets/${full.ticket.id}`} className="text-sm text-primary hover:underline">
                      Ticket {full.ticket.number}
                    </Link>
                  )}
                </div>
                {full.note && <Alert>{full.note}</Alert>}
                <pre className="max-h-[50vh] overflow-auto whitespace-pre-wrap rounded-md border bg-muted/40 p-3 font-sans text-sm">{full.bodyText}</pre>
              </>
            )}
          </DialogBody>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// ---------------------------------------------------------------------------

export function M365Page() {
  const { data, isLoading } = useQuery({ queryKey: KEY, queryFn: () => api<M365Overview>('/integrations/m365') });
  const [tab, setTab] = useState<'setup' | 'log'>('setup');
  const [manual, setManual] = useState(false);
  const [params, setParams] = useSearchParams();

  useEffect(() => {
    const consent = params.get('consent');
    if (!consent) return;
    if (consent === 'ok') toast.success('Administratorzustimmung erteilt. Bitte die Verbindung testen (Berechtigungen können einige Minuten brauchen).');
    else toast.error(`Zustimmung nicht erteilt${params.get('message') ? `: ${params.get('message')}` : ''}`, { duration: 15_000 });
    params.delete('consent');
    params.delete('message');
    setParams(params, { replace: true });
  }, [params, setParams]);

  return (
    <div className="mx-auto max-w-5xl">
      <PageHeader title="Microsoft 365" description="E-Mail-Postfächer, Outlook-Kalender und Anmeldung mit Microsoft – vollständig hier einrichten." />
      <Tabs
        value={tab}
        onChange={setTab}
        items={[
          { value: 'setup', label: 'Einrichtung' },
          { value: 'log', label: 'E-Mail-Verlauf' },
        ]}
      />
      <div className="mt-4 grid gap-6">
        {isLoading || !data ? (
          <Skeleton className="h-96" />
        ) : tab === 'setup' ? (
          <>
            <M365Wizard connected={Boolean(data.connection)} />
            {(data.connection || manual) && <ConnectionCard data={data} />}
            <MailboxesCard data={data} />
            <details className="group rounded-lg border bg-card" open={manual} onToggle={(e) => setManual(e.currentTarget.open)}>
              <summary className="cursor-pointer select-none px-4 py-3 text-sm font-medium">Manuelle Einrichtung (eigene App-Registrierung in Entra ID)</summary>
              <div className="border-t p-4">
                <SetupGuide data={data} />
              </div>
            </details>
          </>
        ) : (
          <EmailLog />
        )}
      </div>
    </div>
  );
}
